import { useEffect, useState } from "react"

// MUI
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"
import List from "@mui/material/List"
import CircularProgress from "@mui/material/CircularProgress"
import Alert from "@mui/material/Alert"
import Snackbar from "@mui/material/Snackbar"
import Switch from "@mui/material/Switch"
import FormControlLabel from "@mui/material/FormControlLabel"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Chip from "@mui/material/Chip"


// Icons
import SearchIcon from '@mui/icons-material/Search'
import CloudIcon from '@mui/icons-material/CloudDone'
import CloseIcon from '@mui/icons-material/Close'

import TestIcon from '@mui/icons-material/GraphicEq'
import ClearIcon from '@mui/icons-material/Clear'

// Custom 
import { PairListItem } from "./PairListItem"
import { Indicators } from "../../Indicators"
import { LottieLoading } from "../../placeholder/LottieLoading"

// Redux
import { useDispatch, useSelector } from "react-redux"
import { clearSettings, setBacktestConfig, setBacktestSettings } from "../../../redux/reducers/backtesting/backtestingReducer"
import { ApplicationState } from "../../../interface/ApplicationState"
import { TakeProfitAndStopLossConfig } from "./TakeProfitAndStopLossConfig"
import { ScheduleConfig } from "./ScheduleConfig"
import { ChartIntervalConfig } from "./ChartIntervalConfig"
import { useFilter } from "./hooks/useFilter"
import { useSaveBacktest } from "./hooks/useSaveBacktest"
import { BuySellLimit } from "./BuySellLimit"
import { getSymbolsBegin } from "../../../redux/actions/userActions"
import { alpha, useTheme } from "@mui/material/styles"

interface CreateCustomBackTestProps
{
  open: boolean
  onClose: () => void
}

export const CreateCustomBackTest = ({ open, onClose }: CreateCustomBackTestProps) => {
  const theme = useTheme()

  const { addResult, handleSave, saving, setAddResult } = useSaveBacktest()
  const [ query, setQuery ] = useState("")
  const symbols = useSelector((state: ApplicationState) => state.userStore.symbols)
  const isGettingSymbols = useSelector((state: ApplicationState) => state.userStore.isGettingSymbols)

  const dispatch = useDispatch()
  const config = useSelector((state: ApplicationState) => state.backtesting.config)
  const settings = useSelector((state: ApplicationState) => state.backtesting)
  const filteredSymbols = useFilter(query, symbols)

  useEffect(() => {
    if(!symbols || symbols.length === 0) dispatch(getSymbolsBegin())
  }, [])

  useEffect(() => {
    if(addResult && addResult.color === "success") setTimeout(() => handleClose(), 3000)
  }, [ addResult ])

  const handleClearSearch = () => {
    setQuery("")
  }
  
  const handleSelectPair = (pair: string) => {
    setQuery("")
    dispatch(setBacktestConfig({ tradingPair: pair }))
    dispatch(setBacktestSettings({ isEnabled: true }))
  } 

  const handleClose = () => {
    dispatch(clearSettings())
    onClose()
  }

  const canSave = () => {
    if(!settings.accountBalanceUsdt || !settings.friendlyName || !settings.schedule || !settings.config?.tradingPair || !settings.config.chartInterval) return false
    return true
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "18px",
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(180deg, ${alpha("#101c2d", 0.98)} 0%, ${alpha("#0a1320", 0.99)} 100%)`
              : `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef5ff", 0.98)} 100%)`,
          border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 24px 60px ${alpha("#01050d", 0.5)}`
              : `0 24px 60px ${alpha("#9db3d1", 0.22)}`,
        }
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: 2, borderBottom: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}` }}>
        <Grid container direction="row" spacing={2} alignItems="center"> 
          <Grid item>
            <Box sx={styles.headerIcon(theme)}>
              <TestIcon />
            </Box>
          </Grid>
          <Grid item flex={1}>
            <Typography variant="overline" sx={{ letterSpacing: 1.5, color: alpha(theme.palette.text.primary, 0.64) }}>
              Backtesting Setup
            </Typography>
            <Typography variant="h5">Create Custom Backtest</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: alpha(theme.palette.text.primary, 0.68) }}>
              Build a saved strategy config, choose its execution schedule, and tune risk settings before running.
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose} sx={styles.closeButton(theme)}><CloseIcon /></IconButton>
          </Grid>
        </Grid>
        </DialogTitle>

      { isGettingSymbols && <LottieLoading animation={require("../../../animations/backtesting-lottie.json")} height={150} width={150} loop text="Getting Trading Pairs" containerHeight="20vh"/>}

     { !isGettingSymbols && <DialogContent sx={{ px: 3, py: 2.5 }}>

        <Grid container direction="column" spacing={2}>

          { !config?.tradingPair && <>
            <Grid item xs={12}>
              <Box sx={styles.sectionCard(theme)}>
              <Typography variant="overline" sx={styles.sectionLabel(theme)}>Start With A Pair</Typography>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Search for a market to begin</Typography>
              <TextField 
                 value={query}
                 onChange={(e: any) => setQuery(e.target.value)}
                 InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1}} />,
                  endAdornment: <IconButton onClick={handleClearSearch}><ClearIcon /></IconButton>
                 }}
                 autoFocus 
                 fullWidth 
                 placeholder="Search for a trading pair to begin" />
                { query && <Typography variant="caption">{`Found ${filteredSymbols.length} trading pairs`}</Typography> }
                </Box>
            </Grid>

            <Grid item>
              <Box sx={styles.sectionCard(theme)}>
              <List style={styles.list}>
                { filteredSymbols.map((item, key) => <PairListItem key={key} item={item} onClick={handleSelectPair} />)}
              </List>
              </Box>
            </Grid></> }


            { config?.tradingPair && <>

            <Grid item>
              <Box sx={styles.heroCard(theme)}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="overline" sx={styles.sectionLabel(theme)}>Backtest Summary</Typography>
                    <Typography variant="h5" sx={{ mb: 1.5 }}>Name the test and confirm the market</Typography>
                    <TextField 
                      fullWidth 
                      placeholder="Custom backtest name - E.G My first backtest" 
                      value={settings.friendlyName} 
                      onChange={(e: any) => dispatch(setBacktestSettings({ friendlyName: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack spacing={1.25} alignItems={{ xs: "flex-start", md: "stretch" }}>
                      <Chip label={config?.tradingPair} sx={styles.summaryChip(theme)} />
                      <Chip label={config?.chartInterval ? `Interval ${config.chartInterval}` : "Interval not set"} sx={styles.summaryChip(theme)} />
                      <Chip label={settings?.schedule ? `Schedule ${settings.schedule}h` : "Schedule not set"} sx={styles.summaryChip(theme)} />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          
            <Grid item>
              <Box sx={styles.sectionCard(theme)}>
                <Typography variant="overline" sx={styles.sectionLabel(theme)}>Selected Pair</Typography>
                <PairListItem onEdit={() => dispatch(setBacktestConfig({ tradingPair: ""}))} isSelected onClick={() => {}} item={{ symbol: config?.tradingPair, baseAsset: "", quoteAsset: "", status: "Select Pair" } } />
              </Box>
            </Grid>

            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={styles.sectionCard(theme)}>
                  <Typography variant="overline" sx={styles.sectionLabel(theme)}>Notifications</Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>Notify Config</Typography>
                  <FormControlLabel value={settings?.notifyAfterComplete} label="Send push message after test is complete" onChange={(_, value) => dispatch(setBacktestSettings({ notifyAfterComplete: value }))} control={<Switch />} />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={styles.sectionCard(theme)}>
                  <BuySellLimit />
                </Box>
              </Grid>
            </Grid>

            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={styles.sectionCard(theme)}>
                  <ChartIntervalConfig />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={styles.sectionCard(theme)}>
                  <ScheduleConfig />
                </Box>
              </Grid>
            </Grid>
          
            <Grid item>
              <Box sx={styles.sectionCard(theme)}>
                <TakeProfitAndStopLossConfig />
              </Box>
            </Grid>

            <Grid item>
              <Box sx={styles.sectionCard(theme)}>
                <Indicators isBacktest />
              </Box>
            </Grid>
        
            </>}

        </Grid>

      </DialogContent> }

      <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`, backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.14 : 0.02) }}>
        { !isGettingSymbols && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} sx={{ width: "100%" }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ color: alpha(theme.palette.text.primary, 0.68) }}>
                Required: test name, trading pair, chart interval, schedule, and account balance.
              </Typography>
            </Box>
            <Button sx={{ minWidth: 220, borderRadius: "12px", boxShadow: "none" }} variant="contained" size="large" onClick={() => handleSave(settings)} disabled={saving || !canSave()} startIcon={saving ? <CircularProgress size={16} /> : <CloudIcon />}>{ saving ? "Saving" : "Save Backtest" }</Button>
          </Stack>
        ) }
      </DialogActions>

      { addResult && <Snackbar open autoHideDuration={5000} onClose={() => setAddResult(undefined)} >
        <Alert variant="filled" severity={addResult.color}>{addResult?.message}</Alert>
      </Snackbar> }

    </Dialog>
  )
}

export const styles = {
  list: {
    overflow: "auto" as "auto",
    height: 300
  },
  heroCard: (theme: any) => ({
    p: 2.5,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#102946", 0.7)} 0%, ${alpha("#0c1727", 0.85)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.9)} 0%, ${alpha("#e8f2ff", 0.9)} 100%)`,
  }),
  sectionCard: (theme: any) => ({
    p: 2,
    borderRadius: "14px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.58),
  }),
  sectionLabel: (theme: any) => ({
    display: "block",
    mb: 0.75,
    letterSpacing: 1.4,
    color: alpha(theme.palette.text.primary, 0.62),
  }),
  summaryChip: (theme: any) => ({
    justifyContent: "flex-start",
    borderRadius: "999px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.72),
    color: theme.palette.text.primary,
  }),
  headerIcon: (theme: any) => ({
    width: 44,
    height: 44,
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    color: theme.palette.info.main,
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.14),
  }),
  closeButton: (theme: any) => ({
    width: 40,
    height: 40,
    borderRadius: "12px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.6)}`,
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.04 : 0.7),
  }),
  testTube: {
    marginTop: 2,
  },
  card: {
    padding: 16,
    borderRadius: 14,
    border: `1px solid ${alpha("#ffffff", 0.08)}`,
    backgroundColor: alpha("#000000", 0.18),
    boxShadow: "none",
  },
  switchItem: {
    paddingLeft: 16,
    paddingRight: 16,
    marginBottom: 10,
  }
}
